// src/features/secret/SealPanel.tsx
// Header banner for every /secrets/* page: seal status glance, initialize (first run), unseal
// (blocks the rest of the console while sealed — matches real Vault behavior), seal (admin).

import { useEffect, useState, useCallback } from 'react';
import { useI18n, useSecretSeal } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, ErrorAlert } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { TypedConfirmDialog } from './TypedConfirmDialog';
import type { SealStatus } from '@core/types/secret';

function useCopyToClipboard() {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard unavailable */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);
  return { copied, copy };
}

function InitializeForm({ onInitialized }: { onInitialized: () => void }) {
  const { t } = useI18n();
  const store = useSecretSeal();
  const [shares, setShares] = useState('5');
  const [threshold, setThreshold] = useState('3');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<{ keys: string[]; rootToken: string } | null>(null);
  const { copied, copy } = useCopyToClipboard();

  const sharesValid = /^\d+$/.test(shares.trim()) && parseInt(shares, 10) > 0;
  const thresholdValid = /^\d+$/.test(threshold.trim()) && parseInt(threshold, 10) > 0;

  const handleInit = async () => {
    if (!sharesValid || !thresholdValid) return;
    setIsSubmitting(true);
    try {
      const resp = await store.Initialize({
        secret_shares: parseInt(shares, 10),
        secret_threshold: parseInt(threshold, 10),
      });
      setResult({ keys: resp.keys, rootToken: resp.root_token });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (result) {
    const blob = [`root_token: ${result.rootToken}`, ...result.keys.map((k, i) => `unseal_key_${i + 1}: ${k}`)].join('\n');
    return (
      <Alert variant="success">
        <div className="space-y-3">
          <p className="text-sm font-medium">{t('secret.seal.initializedTitle')}</p>
          <p className="text-xs text-muted-foreground">{t('secret.seal.initializedWarning')}</p>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t('secret.seal.rootToken')}</p>
            <code className="block px-3 py-2 rounded-md bg-muted text-foreground font-mono text-sm break-all select-all">{result.rootToken}</code>
          </div>
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">{t('secret.seal.unsealKeys', { count: result.keys.length })}</p>
            {result.keys.map((k, i) => (
              <code key={i} className="block px-3 py-1.5 rounded-md bg-muted text-foreground font-mono text-xs break-all select-all">{k}</code>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => copy(blob)}>
              {copied ? t('common.copied') : t('secret.seal.copyAll')}
            </Button>
            <Button size="sm" onClick={onInitialized}>{t('secret.seal.continueToUnseal')}</Button>
          </div>
        </div>
      </Alert>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('secret.seal.initializeTitle')}</CardTitle>
        <CardDescription>{t('secret.seal.initializeDescription')}</CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4">
        <Input
          label={t('secret.seal.shares')}
          value={shares}
          onChange={(e) => setShares(e.target.value.replace(/[^0-9]/g, ''))}
          inputMode="numeric"
          error={!shares.trim() || sharesValid ? undefined : t('secret.validation.positiveIntegerRequired')}
        />
        <Input
          label={t('secret.seal.threshold')}
          value={threshold}
          onChange={(e) => setThreshold(e.target.value.replace(/[^0-9]/g, ''))}
          inputMode="numeric"
          error={!threshold.trim() || thresholdValid ? undefined : t('secret.validation.positiveIntegerRequired')}
        />
      </CardContent>
      <CardFooter className="flex justify-end">
        <Button onClick={handleInit} disabled={isSubmitting || !sharesValid || !thresholdValid}>
          {isSubmitting ? t('common.loading') : t('secret.seal.initialize')}
        </Button>
      </CardFooter>
    </Card>
  );
}

function UnsealForm({ status, onUnsealed }: { status: SealStatus; onUnsealed: (s: SealStatus) => void }) {
  const { t } = useI18n();
  const store = useSecretSeal();
  const [key, setKey] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!key.trim()) return;
    setIsSubmitting(true);
    try {
      const resp = await store.Unseal({ key: key.trim() });
      setKey('');
      if (resp.seal_status) onUnsealed(resp.seal_status);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="border-warning/50">
      <form onSubmit={handleSubmit}>
        <CardHeader>
          <CardTitle>{t('secret.seal.sealedTitle')}</CardTitle>
          <CardDescription>
            {t('secret.seal.progress', { progress: status.progress, threshold: status.threshold })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Input
            label={t('secret.seal.unsealKeyLabel')}
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            required
          />
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button type="submit" disabled={isSubmitting || !key.trim()}>
            {isSubmitting ? t('common.loading') : t('secret.seal.unseal')}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

export interface SealPanelProps {
  /** Rendered only once the vault is confirmed initialized and unsealed. */
  children: React.ReactNode;
}

export function SealPanel({ children }: SealPanelProps) {
  const { t } = useI18n();
  const store = useSecretSeal();
  const [status, setStatus] = useState<SealStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [health, setHealth] = useState<{ version: string; clusterName: string } | null>(null);
  const [sealConfirmOpen, setSealConfirmOpen] = useState(false);
  const [isSealing, setIsSealing] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    store.GetSealStatus()
      .then((resp) => setStatus(resp.seal_status ?? null))
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [store]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    store.Health().then((r) => setHealth({ version: r.version, clusterName: r.cluster_name })).catch(() => {});
  }, [store]);

  const doSeal = async () => {
    setIsSealing(true);
    try {
      await store.Seal({ cluster_id: status?.cluster_id ?? '' });
      setSealConfirmOpen(false);
      load();
    } finally {
      setIsSealing(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  if (store.error != null && !status) {
    return (
      <div className="p-6">
        <ErrorAlert error={store.error} />
      </div>
    );
  }

  if (!status?.initialized) {
    return (
      <div className="p-6 max-w-lg mx-auto">
        <InitializeForm onInitialized={load} />
      </div>
    );
  }

  if (status.sealed) {
    return (
      <div className="p-6 max-w-lg mx-auto">
        <UnsealForm status={status} onUnsealed={setStatus} />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between gap-4 border-b border-border bg-muted/30 px-6 py-2">
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          <StatusBadge variant="success" label={t('secret.seal.unsealedBadge')} size="sm" />
          {health && <span>{health.clusterName} · v{health.version}</span>}
        </div>
        <button
          onClick={() => setSealConfirmOpen(true)}
          className="text-xs text-destructive hover:underline"
        >
          {t('secret.seal.sealAction')}
        </button>
      </div>

      <TypedConfirmDialog
        open={sealConfirmOpen}
        title={t('secret.seal.sealConfirmTitle')}
        description={t('secret.seal.sealConfirmDescription')}
        phrase={status.cluster_name || 'seal'}
        confirmLabel={t('secret.seal.sealAction')}
        isConfirming={isSealing}
        onConfirm={doSeal}
        onCancel={() => setSealConfirmOpen(false)}
      />

      {children}
    </div>
  );
}
